import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import Breadcrumbs from "@/components/Breadcrumbs";
import { collegeLd, breadcrumbLd, faqLd } from "@/lib/seo";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import VerifiedInstitutionHero from "@/components/VerifiedInstitutionHero";
import AICtaBlock from "@/components/AICtaBlock";
import CampusTruthCollegeSection from "@/components/RealTalk/CampusTruthCollegeSection";
import { track } from "@/lib/analytics";
import {
  MapPin, Globe, GraduationCap, BookOpen, ArrowLeft, Loader2, ExternalLink,
  Building2, ShieldCheck, FileText, Phone, MessageCircle, Sparkles, CheckCircle2,
  Target, Lightbulb, AlertCircle, ArrowRight, IndianRupee, ClipboardList,
} from "lucide-react";

const clean = (v) => { const s = String(v ?? "").trim(); return !s || s.toLowerCase() === "pending" ? null : s; };
const WA = (process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "");

function decisionInsights(c, courses) {
  const own = (clean(c.Ownership) || "").toLowerCase();
  const cat = clean(c.Primary_Category) || clean(c.Vertical) || "higher education";
  const tier = (c.Inclusion_Tier || "").toLowerCase();
  const city = clean(c.City);
  const whoFit = [];
  if (own.includes("govern")) whoFit.push("students seeking an affordable, government-backed institution");
  if (own.includes("private") || own.includes("deemed")) whoFit.push("students who value modern campus facilities and industry tie-ups");
  if (cat) whoFit.push(`aspirants focused on ${cat.toLowerCase()} programmes`);
  if (city) whoFit.push(`students who want to study in ${city}`);
  const strengths = [];
  if (tier.includes("tier 1")) strengths.push("Recognised among the top-tier institutions in our index");
  if (courses?.length) strengths.push(`${courses.length} programme${courses.length > 1 ? "s" : ""} on offer`);
  if (clean(c.Official_Website)) strengths.push("Verified official presence");
  if (city && clean(c.State)) strengths.push(`Located in ${city}, ${c.State}`);
  return {
    who: whoFit.slice(0, 3),
    strengths: strengths.slice(0, 4),
    knowBefore: [
      "Confirm the latest fees, scholarships and eligibility for your course before applying.",
      "Check placement records and recruiters relevant to your branch.",
      "Speak to an admission advisor to compare with similar colleges.",
    ],
  };
}

export default function CollegePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [modal, setModal] = useState(null); // intent string
  const lastViewed = useRef(null);

  useEffect(() => {
    setData(null); setErr(false);
    window.scrollTo(0, 0);
    api.get(`/college/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  useEffect(() => {
    const col = data?.college;
    if (col && lastViewed.current !== col.College_ID) {
      lastViewed.current = col.College_ID;
      track("college_view", {
        college_id: col.College_ID, college_name: col.College_Name,
        state: col.State, city: col.City, ownership: col.Ownership,
        category: col.Primary_Category, stream: col.Vertical,
      });
    }
  }, [data]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <p className="text-slate-500">College not found.</p>
      <Link to="/colleges" className="mt-4 inline-flex items-center gap-1 text-[#0B2A6B] font-semibold"><ArrowLeft className="h-4 w-4" /> Back to search</Link>
    </div>
  );
  if (!data) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-[#0B2A6B]" /></div>;

  const c = data.college;
  const courses = data.courses || [];
  const evParams = {
    college_id: c.College_ID, college_name: c.College_Name, state: c.State,
    city: c.City, ownership: c.Ownership, category: c.Primary_Category, stream: c.Vertical,
  };
  const CTA_EVENT = { apply: "apply_now_click", admission_guidance: "admission_guidance_click", information_pack: "info_pack_click", callback: "callback_click" };
  const openModal = (intent) => { track(CTA_EVENT[intent] || "cta_click", { ...evParams, intent }); setModal(intent); };
  const img = clean(data.media?.Primary_Display_Image) || clean(data.image?.Primary_Campus_Image_URL);
  const heroImg = clean(data.heroImage);
  const hasPhoto = !!heroImg;
  const logo = clean(data.media?.Logo_URL) || clean(data.image?.Logo_URL);
  const website = clean(c.Official_Website);
  const verified = !!(clean(c.Verification_Status) || clean(c.Trust_Audit_Status) || website);
  const ins = decisionInsights(c, courses);
  const vihFacts = [
    clean(c.State) && { label: "State", value: c.State },
    clean(c.City) && { label: "City", value: c.City },
    (clean(c.Primary_Category) || clean(c.Vertical)) && { label: "Institution Type", value: clean(c.Primary_Category) || clean(c.Vertical) },
    clean(c.Ownership) && { label: "Ownership", value: c.Ownership.split("(")[0].trim() },
    courses.length && { label: "Major Courses", value: `${courses.length} programme${courses.length > 1 ? "s" : ""}` },
    { label: "AI Guidance", value: "Available" },
  ].filter(Boolean).slice(0, 6);
  const vihSignals = { website: !!website, identity: verified, aiChecked: true, academicAudited: courses.length > 0, updated: true };
  const vihVariant = new URLSearchParams(window.location.search).get("vih") === "wallet" ? "wallet" : "certificate";
  const cardData = { collegeId: c.College_ID, College_Name: c.College_Name, name: c.College_Name, slug, City: c.City, State: c.State, city: c.City, state: c.State };
  const related = data.relatedColleges || [];

  const path = `/college/${slug}`;
  const stateSlug = c.stateSlug;
  const citySlug = c.citySlug;
  const crumbs = [
    { name: "Home", path: "/" },
    ...(stateSlug ? [{ name: c.State, path: `/state/${stateSlug}` }] : []),
    ...(citySlug ? [{ name: c.City, path: `/city/${citySlug}` }] : []),
    { name: c.Short_Name || c.College_Name, path },
  ];
  const faqs = [
    courses.length && { q: `What courses does ${c.College_Name} offer?`, a: `${c.College_Name} offers ${courses.length} programme${courses.length > 1 ? "s" : ""} including ${courses.slice(0, 5).map((x) => x.Course_Name).filter(Boolean).join(", ")}.` },
    { q: `Where is ${c.College_Name} located?`, a: `${c.College_Name} is located in ${[clean(c.Area), c.City, c.State].filter(Boolean).join(", ")}.` },
    clean(c.Ownership) && { q: `Is ${c.College_Name} a government or private institution?`, a: `${c.College_Name} is a ${c.Ownership} institution.` },
    { q: `How can I apply for admission to ${c.College_Name}?`, a: `You can request free admission guidance on this page. Our advisors help you with eligibility, the application process, fees, scholarships and placements for ${c.College_Name}.` },
  ].filter(Boolean);
  const seoJsonLd = [
    collegeLd(c, path, img),
    breadcrumbLd(crumbs),
    faqs.length ? faqLd(faqs) : null,
  ].filter(Boolean);

  const waText = encodeURIComponent(
    `Hi, I am interested in admission to ${c.College_Name}. Please guide me regarding:\n• Eligibility\n• Admission Process\n• Fees\n• Scholarships\n• Placements\n• Career Opportunities`
  );
  const waHref = `https://wa.me/${WA}?text=${waText}`;

  return (
    <div data-testid="mid-college-page" className="pb-24 lg:pb-0">
      <SEOHead
        title={`${c.College_Name} — Admissions, Courses & Guidance`}
        description={`${c.College_Name}, ${c.City}, ${c.State}. Courses, eligibility, fees guidance and free admission counselling.`}
        canonicalPath={path}
        image={img || undefined}
        jsonLd={seoJsonLd}
      />

      {/* Hero / Decision Summary */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0B2A6B] to-[#13367f] text-white">
        {heroImg && <img src={heroImg} alt={c.College_Name} loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="pointer-events-none absolute inset-0 vih-grid opacity-30" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid lg:grid-cols-[1fr_400px] gap-8 lg:items-center">
            {/* Left — identity + CTAs */}
            <div>
              <Breadcrumbs items={crumbs} dark />
              <Link to="/colleges" className="mt-2 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to search</Link>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {verified ? (
                  <span data-testid="verified-badge" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-3 py-1 text-xs font-bold"><ShieldCheck className="h-3.5 w-3.5" /> Verified Listing</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-medium"><AlertCircle className="h-3.5 w-3.5" /> Listing in verification</span>
                )}
                {clean(c.Vertical) && <span className="rounded-full bg-[#D4A24C] px-3 py-1 text-xs font-semibold">{c.Vertical}</span>}
              </div>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold leading-tight">{c.College_Name}</h1>
              {logo && (
                <img src={logo} alt={`${c.College_Name} logo`} loading="lazy" data-testid="college-logo"
                  className="mt-3 h-14 w-auto max-w-[160px] rounded-lg bg-white/95 object-contain p-1.5 shadow-sm" />
              )}
              <p className="mt-2 flex items-center gap-1 text-white/80"><MapPin className="h-4 w-4" /> {clean(c.Area) ? `${c.Area}, ` : ""}{c.City}, {c.State}</p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/75">
                {clean(c.Ownership) && <span>{c.Ownership}</span>}
                {clean(c.Primary_Category) && <span>{c.Primary_Category}</span>}
                <span>{courses.length} course{courses.length === 1 ? "" : "s"}</span>
                <span>Data: MID v1.0 · recently verified</span>
              </div>

              {/* Primary CTAs (desktop visible) */}
              <div className="mt-6 hidden lg:flex flex-wrap gap-3" data-testid="college-cta-row">
                <Link to="/ai-counseling" onClick={() => track("talk_to_ai_click", { ...evParams, location: "college_hero" })} data-testid="college-fit-check" className="rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#081E4D]">Start Free AI College Fit Check</Link>
                <button onClick={() => openModal("admission_guidance")} data-testid="college-guidance" className="rounded-lg bg-[#D4A24C] px-5 py-2.5 text-sm font-bold text-white hover:opacity-90">Get Admission Guidance for This College</button>
                <button onClick={() => openModal("information_pack")} data-testid="college-fees-brochure" className="rounded-lg border border-white/30 px-5 py-2.5 text-sm font-semibold hover:bg-white/10">Get Fees & Brochure</button>
                <Link to="/products/expert-call" onClick={() => track("expert_counselling_click", { ...evParams, location: "college_hero" })} data-testid="college-expert-call" className="rounded-lg border border-white/30 px-5 py-2.5 text-sm font-semibold hover:bg-white/10">Book Expert Call – ₹499</Link>
                {WA && <a href={waHref} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { ...evParams, location: "hero" })} data-testid="whatsapp-cta" className="inline-flex items-center gap-1 rounded-lg bg-[#25D366] px-5 py-2.5 text-sm font-bold hover:opacity-90"><MessageCircle className="h-4 w-4" /> Chat on WhatsApp</a>}
              </div>
            </div>

            {/* Right — verified campus photo OR premium Verified Institution Hero */}
            <div className="lg:pl-2">
              {hasPhoto ? (
                <div data-testid="campus-photo-panel" className="group relative overflow-hidden rounded-2xl border border-white/15 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.55)] min-h-[240px] lg:min-h-[300px]">
                  <img src={heroImg} alt={`${c.College_Name} campus`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ position: "absolute", inset: 0 }} />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1 text-[11px] font-bold text-white backdrop-blur-md">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Verified Campus Photo
                  </span>
                </div>
              ) : (
                <VerifiedInstitutionHero name={c.College_Name} facts={vihFacts} signals={vihSignals} variant={vihVariant} />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Student Decision Panel */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="decision-panel">
            <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Sparkles className="h-5 w-5 text-[#D4A24C]" /> Should you choose {c.Short_Name || "this college"}?</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Insight icon={Target} title="Who should choose this college" items={ins.who} />
              <Insight icon={Lightbulb} title="Key strengths" items={ins.strengths} />
            </div>
            <div className="mt-5">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700"><AlertCircle className="h-4 w-4 text-amber-500" /> What to know before applying</h3>
              <ul className="space-y-1.5 text-sm text-slate-600">
                {ins.knowBefore.map((k, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{k}</li>)}
              </ul>
            </div>
            <div className="mt-5 rounded-xl bg-[#0B2A6B]/5 p-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-[#0B2A6B]">Not sure if this is the right fit? Get a free, personalised recommendation.</p>
              <button onClick={() => openModal("admission_guidance")} className="rounded-lg bg-[#0B2A6B] px-4 py-2 text-sm font-semibold text-white">Get Free Guidance</button>
            </div>
          </div>

          {/* Trust / Overview */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="trust-block">
            <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Building2 className="h-5 w-5 text-[#0B2A6B]" /> College Information</h2>
            <dl className="grid sm:grid-cols-2 gap-4 text-sm">
              <Field label="Ownership" value={clean(c.Ownership)} />
              <Field label="Primary Category" value={clean(c.Primary_Category)} />
              <Field label="Stream / Vertical" value={clean(c.Vertical)} />
              <Field label="Location" value={`${c.City}, ${c.State}`} />
              <Field label="Official Website" value={website} link={website} />
              <Field label="Verification" value={verified ? "Verified via MID v1.0" : "In verification"} />
            </dl>
            <p className="mt-4 text-xs text-slate-400">We show only verified data from our Master Intelligent Database. Fields marked "—" are being verified and will be updated — we never fabricate information.</p>
          </div>

          {/* Verified fees & cutoffs (data-accuracy-first — no fabrication) */}
          <VerifiedDataBlock data={data.verifiedData} college={c} evParams={evParams} />

          <AICtaBlock ctaId="college_detail" headline={`Want to know if ${c.Short_Name || "this college"} is right for YOU?`} sub="Get a free AI recommendation matched to your marks, budget & goals." buttonText="Get AI Recommendation" />

          {/* Courses */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><BookOpen className="h-5 w-5 text-[#0B2A6B]" /> Courses Available ({courses.length})</h2>
            {courses.length === 0 ? <p className="text-sm text-slate-400">Course list is being verified for this college.</p> : (
              <div className="space-y-2">
                {courses.map((co) => (
                  <div key={co.Course_ID} className="rounded-lg border border-slate-200 p-3" data-testid={`course-${co.Course_ID}`}>
                    <p className="font-semibold text-slate-900">{co.Course_Name} {clean(co.Specialization) && <span className="font-normal text-slate-500">· {co.Specialization}</span>}</p>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500">
                      {clean(co.Course_Level) && <span>Level: {co.Course_Level}</span>}
                      {clean(co.Entrance_Exam) && <span>Exam: {co.Entrance_Exam}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {related.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="related-colleges">
              <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Building2 className="h-5 w-5 text-[#0B2A6B]" /> Related Colleges {c.City ? `in ${c.City}` : ""}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/college/${r.slug}`} data-testid={`related-${r.slug}`} className="group flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-3 hover:border-[#0B2A6B]/40 hover:bg-slate-50">
                    <div>
                      <p className="font-semibold text-slate-800 group-hover:text-[#0B2A6B]">{r.name}</p>
                      <p className="text-xs text-slate-500">{[r.city, r.state].filter(Boolean).join(", ")}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-[#0B2A6B]" />
                  </Link>
                ))}
              </div>
            </div>
          )}
          {/* Campus Truth — real student experiences for this college (approved only). */}
          <CampusTruthCollegeSection collegeName={c.College_Name} />
        </div>

        {/* Sidebar lead panel */}
        <aside className="space-y-4">
          <div className="rounded-2xl border-2 border-[#0B2A6B]/15 bg-white p-5 lg:sticky lg:top-6">
            <h3 className="font-display font-bold text-slate-900">Get admission help — free</h3>
            <p className="mt-1 text-sm text-slate-500">Our advisors guide you on eligibility, fees, scholarships & placements.</p>
            <div className="mt-4 space-y-2">
              <button onClick={() => openModal("apply")} data-testid="side-apply" className="w-full rounded-lg bg-[#0B2A6B] py-2.5 text-sm font-bold text-white hover:bg-[#081E4D]">🎓 Apply Now</button>
              <button onClick={() => openModal("information_pack")} data-testid="side-infopack" className="w-full rounded-lg border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">📄 Get FREE Information Pack</button>
              <button onClick={() => openModal("callback")} data-testid="side-callback" className="w-full rounded-lg border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center justify-center gap-1"><Phone className="h-4 w-4" /> Request Call Back</button>
              {WA && <a href={waHref} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { ...evParams, location: "sidebar" })} className="block w-full rounded-lg bg-[#25D366] py-2.5 text-center text-sm font-bold text-white hover:opacity-90">💬 WhatsApp Counsellor</a>}
            </div>
          </div>
        </aside>
      </div>

      {/* Sticky mobile CTA bar (Fit Check · WhatsApp · Expert Call) */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex gap-2" data-testid="mobile-sticky-cta">
        <Link to="/ai-counseling" onClick={() => track("talk_to_ai_click", { ...evParams, location: "sticky_mobile" })} className="flex-1 rounded-lg bg-[#0B2A6B] py-2.5 text-center text-sm font-bold text-white">Free AI Check</Link>
        {WA && <a href={waHref} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { ...evParams, location: "sticky_mobile" })} className="flex-1 rounded-lg bg-[#25D366] py-2.5 text-center text-sm font-bold text-white">WhatsApp</a>}
        <Link to="/products/expert-call" onClick={() => track("expert_counselling_click", { ...evParams, location: "sticky_mobile" })} className="flex-1 rounded-lg border border-[#0B2A6B] py-2.5 text-center text-sm font-bold text-[#0B2A6B]">Expert ₹499</Link>
      </div>

      <LeadCaptureModal open={!!modal} intent={modal} college={cardData} onClose={() => setModal(null)} />
    </div>
  );
}

function Insight({ icon: Icon, title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700"><Icon className="h-4 w-4 text-[#0B2A6B]" /> {title}</h3>
      <ul className="space-y-1.5 text-sm text-slate-600">
        {items.map((t, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{t}</li>)}
      </ul>
    </div>
  );
}
function Field({ label, value, link }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 font-medium text-slate-800 break-words">
        {link ? <a href={link.startsWith("http") ? link : `https://${link}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B2A6B] hover:underline">{value} <ExternalLink className="h-3 w-3" /></a> : (value || "—")}
      </dd>
    </div>
  );
}

// Verified fees & cutoffs — displays only when data_status === 'verified'.
// Never fabricates: shows the honest "being verified" state otherwise.
function VerifiedDataBlock({ data, college, evParams }) {
  const fee = data?.fee;
  const cutoffs = data?.cutoffs;
  const feeVerified = fee?.status === "verified" && fee?.value;
  const cutoffsVerified = cutoffs?.status === "verified" && (cutoffs?.items || []).length > 0;
  const fired = useRef(false);
  useEffect(() => {
    if ((feeVerified || cutoffsVerified) && !fired.current) {
      fired.current = true;
      track("college_verified_data_viewed", {
        ...evParams,
        has_verified_fee: !!feeVerified,
        has_verified_cutoffs: !!cutoffsVerified,
      });
    }
  }, [feeVerified, cutoffsVerified, evParams]);

  const fmtINR = (n) => {
    if (n == null || isNaN(Number(n))) return "";
    const v = Number(n);
    if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
    return `₹${v.toLocaleString("en-IN")}`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short" }); } catch { return ""; }
  };

  if (!feeVerified && !cutoffsVerified) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="verified-data-block-unavailable">
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <IndianRupee className="h-5 w-5 text-[#0B2A6B]" /> Fees & Cutoffs
        </h2>
        <p className="text-sm text-slate-500">
          {data?.unavailable_message || "Fee/cutoff information is being verified."}
        </p>
        <p className="mt-2 text-xs text-slate-400">
          We publish these only after cross-checking the official {college?.Short_Name || "college"} source. Ask an advisor for the latest confirmed numbers.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="verified-data-block-verified">
      <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900">
        <ShieldCheck className="h-5 w-5 text-emerald-600" /> Verified Fees & Cutoffs
      </h2>
      {feeVerified && (
        <div className="mb-4" data-testid="verified-fee-row">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <IndianRupee className="h-4 w-4 text-[#0B2A6B]" /> Annual fees
          </p>
          <p className="mt-1 text-base font-bold text-slate-900">
            {fmtINR(fee.value.min)}{fee.value.max && fee.value.max !== fee.value.min ? ` – ${fmtINR(fee.value.max)}` : ""}
            <span className="ml-2 text-xs font-normal text-slate-500">{fee.value.currency || "INR"}</span>
          </p>
          {(fee.source || fee.verified_at) && (
            <p className="mt-1 text-xs text-slate-500">
              {fee.source && (
                <a href={fee.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B2A6B] hover:underline">Source <ExternalLink className="h-3 w-3" /></a>
              )}
              {fee.source && fee.verified_at ? " · " : ""}
              {fee.verified_at ? `Verified ${fmtDate(fee.verified_at)}` : ""}
            </p>
          )}
        </div>
      )}
      {cutoffsVerified && (
        <div data-testid="verified-cutoff-row">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <ClipboardList className="h-4 w-4 text-[#0B2A6B]" /> Recent cutoffs
          </p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {(cutoffs.items || []).slice(0, 6).map((r, i) => (
              <li key={i} className="flex justify-between gap-3 border-b border-slate-100 py-1 last:border-b-0">
                <span className="truncate">{[r.exam, r.course, r.category].filter(Boolean).join(" · ") || "Cutoff"}</span>
                <span className="font-semibold text-slate-900">{r.value || r.rank || r.percentile || "—"}</span>
              </li>
            ))}
          </ul>
          {(cutoffs.source || cutoffs.verified_at) && (
            <p className="mt-2 text-xs text-slate-500">
              {cutoffs.source && (
                <a href={cutoffs.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0B2A6B] hover:underline">Source <ExternalLink className="h-3 w-3" /></a>
              )}
              {cutoffs.source && cutoffs.verified_at ? " · " : ""}
              {cutoffs.verified_at ? `Verified ${fmtDate(cutoffs.verified_at)}` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
